/**
 * Application-wide logging module using Pino
 * Pinoを使用したアプリケーション全体のロギングモジュール
 *
 * @module logger
 *
 * @description
 * Provides a configured Pino logger instance with environment-specific settings.
 * - Test environment: Minimal output for cleaner test results
 * - Development environment: Pretty-printed, colorized output
 * - Production environment: Structured JSON logs with sensitive data redaction
 * - Client-side: Falls back to console methods
 *
 * 環境固有の設定を持つ設定済みのPinoロガーインスタンスを提供します。
 * - テスト環境：よりクリーンなテスト結果のための最小限の出力
 * - 開発環境：整形された、色付きの出力
 * - 本番環境：機密データの編集を伴う構造化されたJSONログ
 * - クライアントサイド：consoleメソッドにフォールバック
 */
import pino, { type Logger } from 'pino';

/**
 * Check if running in browser environment
 * ブラウザ環境で実行されているかチェック
 * @private
 */
const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';

/**
 * Environment detection constants
 * 環境検出定数
 * @private
 */
const isDevelopment = !isBrowser && process.env.NODE_ENV === 'development';
const isTest = !isBrowser && process.env.NODE_ENV === 'test';

/**
 * Browser-compatible logger interface
 * ブラウザ互換のロガーインターフェース
 */
interface BrowserLogger {
  debug: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  child: () => BrowserLogger;
  isLevelEnabled: () => boolean;
}

/**
 * Create a browser-compatible console logger
 * ブラウザ互換のコンソールロガーを作成
 *
 * @private
 * @returns {BrowserLogger} Browser console logger
 */
const createBrowserLogger = (): BrowserLogger => {
  const formatMessage = (level: string, ...args: any[]) => {
    const [objOrMsg, ...rest] = args;
    if (typeof objOrMsg === 'object' && objOrMsg !== null && rest.length > 0) {
      // Pinoスタイル: logger.info({ data }, 'message')
      return [`[${level.toUpperCase()}]`, rest[0], objOrMsg, ...rest.slice(1)];
    }
    return [`[${level.toUpperCase()}]`, ...args];
  };

  return {
    debug: (...args: any[]) => console.log(...formatMessage('debug', ...args)),
    info: (...args: any[]) => console.log(...formatMessage('info', ...args)),
    warn: (...args: any[]) => console.warn(...formatMessage('warn', ...args)),
    error: (...args: any[]) => console.error(...formatMessage('error', ...args)),
    child: () => createBrowserLogger(),
    isLevelEnabled: () => true,
  };
};

/**
 * Create a Pino logger instance with environment-specific configuration
 * 環境固有の設定を持つPinoロガーインスタンスを作成
 *
 * @private
 * @returns {Logger | BrowserLogger} Configured logger instance / 設定済みのロガーインスタンス
 *
 * @remarks
 * Configuration varies by environment / 環境によって設定が異なります:
 * - Browser: Console-based logging / ブラウザ：コンソールベースのロギング
 * - Test: Only errors, minimal format / テスト：エラーのみ、最小限のフォーマット
 * - Development: Debug level, pretty-printed with colors / 開発：デバッグレベル、色付きで整形表示
 * - Production: Info level, JSON format with sensitive data redaction / 本番：情報レベル、機密データ編集を伴うJSON形式
 */
const createLogger = (): Logger | BrowserLogger => {
  // Browser environment: use console
  if (isBrowser) {
    return createBrowserLogger() as unknown as Logger;
  }
  // Test environment: minimal log output for cleaner test results
  if (isTest) {
    return pino({
      level: 'error',
      transport: {
        target: 'pino-pretty',
        options: {
          destination: 1,
          sync: true,
          ignore: 'pid,hostname,time',
          messageFormat: '{msg}',
        },
      },
    });
  }

  // Development environment: pretty print for better readability
  if (isDevelopment) {
    return pino({
      level: (!isBrowser && process.env.LOG_LEVEL) || 'debug',
      transport: {
        target: 'pino-pretty',
        options: {
          colorize: true,
          levelFirst: true,
          translateTime: 'HH:MM:ss.l',
          ignore: 'pid,hostname',
          messageFormat: '{msg}',
        },
      },
    });
  }

  // Production environment: structured JSON format
  return pino({
    level: (!isBrowser && process.env.LOG_LEVEL) || 'info',
    formatters: {
      level: (label) => {
        return { level: label };
      },
    },
    timestamp: pino.stdTimeFunctions.isoTime,
    // redact: {
    //   paths: ['headers.authorization', 'headers.cookie', 'headers["x-session-id"]'],
    //   remove: true,
    // },
  });
};

/**
 * Singleton logger instance for application-wide use
 * アプリケーション全体で使用するシングルトンロガーインスタンス
 *
 * @description
 * Pre-configured logger that adapts to the current environment.
 * - Server-side: Uses Pino with full features
 * - Client-side: Falls back to console methods
 * Use this logger throughout the application for consistent logging.
 *
 * 現在の環境に適応する事前設定されたロガー。
 * - サーバーサイド：フル機能のPinoを使用
 * - クライアントサイド：consoleメソッドにフォールバック
 * 一貫したロギングのため、アプリケーション全体でこのロガーを使用してください。
 *
 * @example
 * ```typescript
 * import { logger } from '~/logger';
 *
 * // Log at different levels / 異なるレベルでログ出力
 * logger.debug('Debug information');
 * logger.info({ userId: 123 }, 'User logged in');
 * logger.warn('Warning message');
 * logger.error(error, 'An error occurred');
 *
 * // Log with structured data / 構造化データでログ出力
 * logger.info({
 *   type: 'api_request',
 *   method: 'POST',
 *   url: '/api/users',
 *   duration: 150
 * }, 'API request completed');
 * ```
 *
 * @remarks
 * - Log level can be configured via LOG_LEVEL environment variable
 *   ログレベルはLOG_LEVEL環境変数で設定可能
 * - In production, sensitive paths like authorization headers are automatically redacted
 *   本番環境では、認証ヘッダーなどの機密パスは自動的に編集されます
 * - All logs include timestamp (ISO format in production)
 *   すべてのログにタイムスタンプが含まれます（本番環境ではISO形式）
 */
export const logger = createLogger();

/**
 * Re-export Logger type for TypeScript usage
 * TypeScript使用のためのLogger型の再エクスポート
 */
export type { Logger } from 'pino';
